import { useCallback, useEffect, useRef, useState } from 'react';
import { trackDemoView } from './demoTracking';

export const prefersReducedMotion = () => {
  try {
    return Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
  } catch {
    return false;
  }
};

/**
 * 단계형 데모 재생기.
 * steps[i] 는 i 단계에 머무는 시간(ms). phase 는 0..steps.length 이고 steps.length 가 완성 화면이다.
 * 재생 전에는 phase 가 -1 이라 `phase === 0` 으로 거는 타이핑·카운트가 화면에 보이기 전에 끝나지 않는다.
 * 화면에 보일 때 처음 재생하고, 움직임 줄이기 설정이거나 IntersectionObserver 가 없으면 완성 화면을 바로 보여준다.
 */
const useDemoPlayer = ({ steps, surface, feature, threshold = 0.35, autoStart = false }) => {
  const ref = useRef(null);
  const last = steps.length;
  const stepsRef = useRef(steps);
  stepsRef.current = steps;
  const [phase, setPhase] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const [paused, setPaused] = useState(false);
  const [reduced] = useState(prefersReducedMotion);
  const startedRef = useRef(false);

  const start = useCallback(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    if (surface) trackDemoView(surface, feature);
    if (reduced) {
      setPhase(last);
      return;
    }
    setPhase(0);
    setPlaying(true);
  }, [feature, last, reduced, surface]);

  useEffect(() => {
    if (autoStart) {
      start();
      return undefined;
    }
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      startedRef.current = true;
      if (surface) trackDemoView(surface, feature);
      setPhase(last);
      return undefined;
    }
    const io = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) {
        start();
        io.disconnect();
      }
    }, { threshold });
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!playing || paused) return undefined;
    if (phase >= last) {
      setPlaying(false);
      return undefined;
    }
    const t = setTimeout(() => setPhase((p) => p + 1), stepsRef.current[phase] || 0);
    return () => clearTimeout(t);
  }, [last, paused, phase, playing]);

  const replay = useCallback(() => {
    startedRef.current = true;
    setPaused(false);
    if (reduced) {
      setPhase(last);
      return;
    }
    setPhase(0);
    setPlaying(true);
  }, [last, reduced]);

  const togglePause = useCallback(() => setPaused((p) => !p), []);

  return {
    ref,
    phase,
    done: phase >= last,
    playing: playing && !paused && phase < last,
    paused,
    replay,
    togglePause,
    reduced,
  };
};

/** active 동안 한 글자씩 늘려 보여준다. complete 면 전체를 보여준다. paused 면 지금 글자에서 멈춘다. */
export const useTypedText = (text, { active, complete, paused = false, charsPerTick = 2, tickMs = 18 }) => {
  const [len, setLen] = useState(0);

  useEffect(() => {
    setLen(complete ? text.length : 0);
  }, [active, complete, text]);

  useEffect(() => {
    if (complete || !active || paused) return undefined;
    const t = setInterval(() => {
      setLen((n) => Math.min(text.length, n + charsPerTick));
    }, tickMs);
    return () => clearInterval(t);
  }, [active, charsPerTick, complete, paused, text, tickMs]);

  return text.slice(0, len);
};

/** active 동안 0 에서 target 까지 tickMs 마다 1씩 센다. complete 면 target. paused 면 지금 숫자에서 멈춘다. */
export const useStepCount = (target, { active, complete, paused = false, tickMs }) => {
  const [n, setN] = useState(0);

  useEffect(() => {
    setN(complete ? target : 0);
  }, [active, complete, target]);

  useEffect(() => {
    if (complete || !active || paused) return undefined;
    const t = setInterval(() => {
      setN((v) => Math.min(target, v + 1));
    }, tickMs);
    return () => clearInterval(t);
  }, [active, complete, paused, target, tickMs]);

  return n;
};

/** 글자 수에 맞춰 타이핑 단계 시간을 잡는다. */
export const typingDuration = (text, charsPerTick = 2, tickMs = 18, tail = 350) => (
  Math.ceil(text.length / charsPerTick) * tickMs + tail
);

export default useDemoPlayer;
